import * as React from "react";
import { Flex, Image } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { AccordionItem } from "components";
import { getImageUrl, ImageType, useCurrentCase, useOnline } from "duck";
import {
  Actions,
  Captions,
  Header,
  Jaw,
  JawWrapper,
  Legend,
  Comments
} from "./components";
import { useGetActionsProps, TOOTH_STATE_TO_BORDER } from "./duck";
import styles from "./DentalChartings.module.css";

const DentalCharting: React.FC = () => {
  const navigate = useNavigate();

  const online = useOnline();
  const upToDate = useCurrentCase(state => state.status === "normal");

  const [selected, setSelected] = React.useState<number[]>([]);

  const { dentalSketch, dentalChartings, milkOrAdultTeeth } = useCurrentCase(
    ({ data }) => ({
      dentalSketch: data.client.patient.dentalSketch,
      dentalChartings: data.client.patient.dentalChartings,
      milkOrAdultTeeth:
        data.client.patient.generalAssessment?.dentalChart || null
    })
  );
  const isBase64DentalSketch = !!(dentalSketch as ImageType)?.base64Format;

  React.useEffect(() => {
    setSelected([]);
    const interactiveTeeth = Array.from(
      document.querySelectorAll("path[data-interactive]")
    );
    const lastClickTimes = new Map<number, number>();

    const handleClick = (event: Event) => {
      const dataValue = (event.target as HTMLElement).dataset.tooth;
      if (!dataValue) {
        return;
      }
      const toothNumber = +dataValue;

      setSelected(prev =>
        prev.includes(toothNumber)
          ? prev.filter(id => id !== toothNumber)
          : [...prev, toothNumber]
      );

      const lastClickTime = lastClickTimes.get(toothNumber) || 0;

      // TODO: Revert dblclick event.
      if (lastClickTime && Date.now() - lastClickTime < 400) {
        event.preventDefault();
        navigate(`./${dataValue}`);
        lastClickTimes.set(toothNumber, 0);
      } else {
        lastClickTimes.set(toothNumber, Date.now());
      }
    };

    interactiveTeeth.forEach(tooth => {
      tooth.addEventListener("click", handleClick);
    });

    return () => {
      interactiveTeeth.forEach(tooth => {
        tooth.removeEventListener("click", handleClick);
      });
    };
  }, [milkOrAdultTeeth]);

  /* eslint-disable no-param-reassign */
  React.useEffect(() => {
    const allTeeth = Array.from(
      document.querySelectorAll("path[data-tooth]")
    ) as HTMLElement[];

    allTeeth.forEach(tooth => {
      const dataValue = tooth.dataset.tooth;
      if (!dataValue) {
        return;
      }
      const isSelected = selected.includes(+dataValue);

      const chart = dentalChartings?.find(cht => cht.tooth === +dataValue);

      if (isSelected) {
        tooth.style.stroke = "black";
      } else {
        tooth.style.stroke =
          TOOTH_STATE_TO_BORDER[chart?.toothState || ""] || "#8592BF";
      }
    });
  }, [selected, dentalChartings]);
  /* eslint-enable */

  const getProps = useGetActionsProps({
    setSelected,
    selected
  });

  if (!dentalChartings || !milkOrAdultTeeth) {
    return null;
  }

  return (
    <AccordionItem title="Dental Charting" noContentPadding>
      <Header isOnline={online && upToDate} />
      <JawWrapper p={{ base: "6", lg: "8" }} className={styles.wrapper}>
        {dentalSketch && (
          <Image
            style={{ pointerEvents: "none" }}
            position="absolute"
            top={{ base: "80px", lg: "88px", xl: "96px" }}
            px={{ base: "6", lg: "8" }}
            w="100%"
            zIndex={1}
            src={
              isBase64DentalSketch
                ? (dentalSketch as ImageType).base64Format
                : getImageUrl(dentalSketch as number)
            }
          />
        )}

        <Flex mb="24px" justifyContent="space-between" w="100%">
          <Actions {...getProps(1)} />
          <Actions {...getProps(2)} flexDirection="row-reverse" />
        </Flex>

        <Comments enabled={false}>
          <Jaw />
          <Captions />
        </Comments>

        <Flex mt="24px" justifyContent="space-between" w="100%">
          <Actions {...getProps(4)} />
          <Actions {...getProps(3)} flexDirection="row-reverse" />
        </Flex>
      </JawWrapper>
      <Legend />
    </AccordionItem>
  );
};

export default DentalCharting;
